import * as React from 'react';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import IconSuccess from '../../assets/Success.svg';
import IconWarning from '../../assets/Warning.svg';

import { AUTOCOMPLETE, INPUTMODE, INPUT_TYPES } from './types';
import { CircularProgress } from '@kmx/legos-react-circular-progress';
import useFocus from '../../../../../src/hooks/useFocus';

interface TextFieldProps {
    /**
     * Controls whether and how the text value should be automatically capitalized as it is entered/edited by the user
     *
     * @default none
     */
    autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';

    /**
     * Provide a hint to the user-agent as to the expected nature of the user input, ideally allowing more sensible autocompletion
     */
    autoComplete?: AUTOCOMPLETE;

    /**
     * Additional CSS class(es) to add to the `<TextField />`
     */
    className?: string;

    /**
     * Attribute indicating the user cannot interact with the `<TextField />`
     *
     * @default false
     */
    disabled?: boolean;

    /**
     * Attribute indicating that there is something loading for the `<TextField />`
     *
     * @default false
     */
    loading?: boolean;

    /**
     * Phrase to render as input control ‘help’ message
     */
    helperText?: string;

    /**
     * Always show ‘help’ message, even when control is not focused
     *
     * @default false
     */
    helperTextIsPersistent?: boolean;

    /**
     * Unique identifier for the `<TextField />` instance. You may pass your own value to override the component’s auto-generated ID.
     */
    id?: string;

    /*
     * The `inputmode` attribute provides a hint to the browser for which type of onscreen keyboard to display. Inputmode is unique to text-input and text-area controls.
     */
    inputMode?: INPUTMODE;

    /*
     * className to pass through to the input control
     */
    inputClassName?: string;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    inputProps?: { [prop: string]: any };

    /*
     * Required accessible label associated with this input control.
     */
    label: string;

    /**
     * The maximum number of characters that the user can enter
     */
    maxLength?: number;

    /**
     * Name of the `<input>`
     */
    name?: string;

    /*
     * Input validation pattern
     */
    pattern?: string;

    /**
     * Boolean attribute indicating the `<input>` should be required
     *
     * @default false
     */
    required?: boolean;

    /*
     * Input type (text, password, tel, etc.)
     */
    type?: INPUT_TYPES;

    /**
     * Value of the `<input>`
     */
    value?: string;

    /**
     * Validation status of the `<input>`
     */
    validationStatus?: 'valid' | 'invalid';

    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;

    /**
     * Optional ref forwarded from the parent
     */
    ref?: React.Ref<HTMLInputElement>;
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>((props, parentRef) => {
    TextField.displayName = 'TextField';
    const {
        autoCapitalize = 'none',
        autoComplete = 'off',
        className,
        disabled = false,
        helperText,
        helperTextIsPersistent = false,
        id: initialId,
        inputMode = 'text',
        inputProps,
        inputClassName,
        validationStatus,
        label,
        maxLength,
        name,
        onBlur,
        onChange,
        onFocus,
        pattern,
        required = false,
        type = 'text',
        value,
        loading = false,
    } = props;

    const [id] = React.useState(initialId || nanoid(8));
    const textFieldId = `text-field-${id}`;
    const helperTextId = `text-field-helper-text-${id}`;
    const internalRef = React.useRef<HTMLInputElement>(null);
    const textFieldRef = (parentRef || internalRef) as React.RefObject<HTMLInputElement>;
    const isFocused = useFocus(textFieldRef);

    return (
        <>
            <div
                className={classNames(className, 'kmx-text-field', 'mdc-text-field', {
                    'mdc-text-field--disabled': disabled,
                    'mdc-text-field--focused': isFocused,
                    'mdc-text-field--invalid': validationStatus === 'invalid',
                    'icon-right': validationStatus !== undefined || loading,
                })}
            >
                <input
                    aria-controls={helperText ? helperTextId : undefined}
                    aria-describedby={helperText ? helperTextId : undefined}
                    autoCapitalize={autoCapitalize}
                    autoComplete={autoComplete}
                    className={`kmx-text-field__input mdc-text-field__input ${inputClassName || ''}`}
                    disabled={disabled}
                    id={textFieldId}
                    inputMode={inputMode}
                    pattern={pattern}
                    maxLength={maxLength}
                    name={name}
                    ref={textFieldRef}
                    required={required}
                    type={type}
                    value={value}
                    onBlur={onBlur}
                    onChange={onChange}
                    onFocus={onFocus}
                    {...inputProps}
                />
                {validationStatus === 'valid' && !loading && (
                    <div className="kmx-text-field-icon--valid">
                        <IconSuccess />
                    </div>
                )}
                {validationStatus === 'invalid' && !loading && (
                    <div className="kmx-text-field-icon">
                        <IconWarning />
                    </div>
                )}
                {loading && (
                    <div className="kmx-text-field-icon--loading">
                        <CircularProgress size="x-small" />
                    </div>
                )}
                <label
                    className={classNames('mdc-floating-label', {
                        'mdc-floating-label--float-above': isFocused || (value !== undefined && value !== ''),
                    })}
                    htmlFor={textFieldId}
                >
                    {label}
                </label>
                <div className={classNames('mdc-line-ripple', { 'mdc-line-ripple--active': isFocused })}></div>
            </div>
            {helperText ? (
                <div
                    className={classNames('kmx-text-field-helper-text', 'mdc-text-field-helper-text', {
                        'mdc-text-field-helper-text--persistent': helperTextIsPersistent,
                        'mdc-text-field-helper-text--validation-msg': validationStatus === 'invalid',
                    })}
                    id={helperTextId}
                >
                    {helperText}
                </div>
            ) : null}
        </>
    );
});
